import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {NEVER, Observable, Subject, switchMap} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../components/destroyable';
import {DEX_EMPTY_ADDR} from '../../components/dex/dex-streaming/const';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {DynUseTokenBalancesOptions} from '../../components/dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {Widget} from '../../components/layout/widget/widget';
import {PARTIAL_LOADING, PartialLoading} from '../../components/partial-loading-indicator';
import {SelectItem} from '../../components/select-input/select-item';
import {FloatWidgetFactory} from '../../pages/dashboard/float-layout/float-widget.factory';
import {AppConfigService} from '../../services/config.service';
import {WidgetFilter} from '../components/widget-filter/widget-filter';
import {DEX_TRANSFER_WIDGET_TYPE_ID} from '../dex-transfer-widget/dex-transfer-widget-type-id';
import {DexTransferWidgetSettingsDef} from '../dex-transfer-widget/settings/dex-transfer-widget-settings';
import {DexWalletWidget} from './dex-wallet-widget';
import {DexWalletWidgetImplementation} from './dex-wallet-widget.implementation';
import {DynWalletGroupingMode} from './dyn-wallet-balances/dyn-wallet-grouping-mode';
import {FilterAssetBySymbolInput} from './filters/filter-by-asset-symbol-input';
import {FilterSupportedAssetsInput} from './filters/filter-supported-assets-input';
import {DexWalletWidgetFilterSettings} from './settings/dex-wallet-widget-settings';

const GROUPING_MODES_MENU: Array<SelectItem<DynWalletGroupingMode>> = [
    {
        value: DynWalletGroupingMode.WALLET,
        label: 'Wallets'
    },
    {
        value: DynWalletGroupingMode.ASSET,
        label: 'Assets'
    }
];

@Component({
    selector:        'dex-wallet-widget',
    templateUrl:     './dex-wallet-widget.component.html',
    styleUrls:       ['./dex-wallet-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders:   [
        FloatWidgetFactory
    ],
    standalone:      false
})
export class DexWalletWidgetComponent implements OnInit {

    @ViewChild('loadingIndicator', {read: PARTIAL_LOADING, static: true})
    public loadingIndicator: PartialLoading;

    public readonly destroy$ = destroy$();

    public readonly widgetImpl: DexWalletWidgetImplementation = this.widget.implementationInstance;

    public dynRemote = this.dynamicZyxDaemonService.dynRemote;

    public staticIsLoading: boolean = false;

    public readonly GROUPING_MODES_MENU = GROUPING_MODES_MENU;

    public readonly widgetFilter = new WidgetFilter<DexWalletWidgetFilterSettings>(this.widget, {
        assetSymbol:           new FilterAssetBySymbolInput(),
        isOnlySupportedAssets: new FilterSupportedAssetsInput()
    });

    public readonly refresh$ = new Subject<void>();

    constructor(
        @Inject(Widget)
        public widget: DexWalletWidget,

        private changeDetectorRef: ChangeDetectorRef,
        private dynamicZyxDaemonService: DynamicZyxDaemonService,
        private dexSecuritiesSubjectService: DexSecuritiesSubjectService,
        private appConfigService: AppConfigService,
        private floatWidgetFactory: FloatWidgetFactory
    ) {

        // TODO
        if (this.appConfigService.isDevOrCiEnv()) {
            (window as any).appConfigService = appConfigService;
        }
    }

    public ngOnInit(): void {
        this.dynamicZyxDaemonService.dynRemote$.pipe(
            switchMap(dynRemote => {
                if (dynRemote !== this.dynRemote) {
                    this.dynRemote = dynRemote;
                    this.changeDetectorRef.markForCheck();
                }

                return dynRemote
                    ? this.listenToDynAuth()
                    : NEVER;
            }),
            takeUntil(this.destroy$)
        ).subscribe();

        this.bindFiltersAndSettings();
    }

    public onTransfer(): void {
        this.floatWidgetFactory.createFloatWidget<DexTransferWidgetSettingsDef>(
            DEX_TRANSFER_WIDGET_TYPE_ID,
            null
        );
    }

    private onFilterChange(): void {
        this.changeDetectorRef.markForCheck();
    }

    private listenToDynAuth(): Observable<any> {
        return this.dynRemote.isLoggedIn$.pipe(
            switchMap(isLoggedIn => {
                if (!isLoggedIn) {
                    this.staticIsLoading = false;
                    this.changeDetectorRef.markForCheck();
                }

                return isLoggedIn
                    ? this.listenToWallets()
                    : NEVER;
            })
        );
    }

    private listenToWallets(): Observable<any> {
        // const userWallets$ = this.dynRemote.userWallets$.pipe(
        //     switchMap(userWallets => {
        //         const balances$ = fromPromise(Promise.all(
        //             userWallets.map(async wallet => ({
        //                 addr: shortenWalletAddress(wallet.address),
        //                 wallet,
        //                 balance: await wallet.getBalance()
        //             }))
        //         ));
        //
        //         return balances$.pipe(
        //             dependOn(defer(() => {
        //                 this.staticIsLoading = false;
        //                 this.changeDetectorRef.markForCheck();
        //
        //                 return this.loadingIndicator.loading$;
        //             }))
        //         );
        //     }),
        //     tap(balances => {
        //         console.warn(balances); // TODO
        //     })
        // );

        // TODO
        // const tokenBalancesOptionsSetter$ = this.dexSecuritiesSubjectService.deliverablesUpdated$.pipe(
        //     combineLatestWith(this.dynRemote.userWallets$),
        //     tap(() => {
        //         const options = this.createTokenBalancesOptions();
        //         console.warn({tokenBalancesOptions: options});
        //
        //         this.dynRemote.tokenBalancesOptions$.next(options);
        //     })
        // );
        // const tokenBalances$ = this.dynRemote.tokenBalances$.pipe(
        //     tap(tokenBalances =>
        //         console.warn({tokenBalances}, tokenBalances && [
        //             tokenBalances.isLoading && 'loading',
        //             tokenBalances.error && 'error',
        //             `n:${tokenBalances.tokenBalances.length}`
        //         ].filter(Boolean).join(', '))
        //     )
        // );
        // const allWalletsTokenBalances$ = this.dynRemote.allWalletsTokenBalances$.pipe(
        //     tap(allWalletsTokenBalances => console.warn({allWalletsTokenBalances}))
        // );
        // const evmProviders$ = this.dynRemote.evmProviders$.pipe(
        //     tap(evmProviders => console.warn({evmProviders}))
        // );

        // const tokenBalances$ = this.dynRemote.userWallets$.pipe(
        //     switchMap(userWallets => {
        //         const addresses = userWallets.map(w => w.address);
        //
        //         return this.dynBalancesService.fetchWalletsTokenBalances(addresses);
        //     }),
        //     tap(balances => console.warn(balances))
        // );

        // return merge(
        //     userWallets$, // TODO
        //     tokenBalances$, // TODO
        //     tokenBalancesOptionsSetter$,
        //     // allWalletsTokenBalances$, // TODO
        //     evmProviders$ // TODO
        // );

        return NEVER;
    }

    private bindFiltersAndSettings(): void {
        this.widgetFilter.bindFiltersAndSettings().pipe(
            tap(() => this.onFilterChange()),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private createTokenBalancesOptions(): DynUseTokenBalancesOptions {
        const chain = this.appConfigService.appConfig.dex.chain;

        const tokenAddresses: string[] = [];
        for (const d of this.dexSecuritiesSubjectService.deliverableMapById.values()) {
            if (d.identifier !== DEX_EMPTY_ADDR) {
                tokenAddresses.push(d.identifier);
            }
        }

        return {
            // TODO
            // networkId: chain.id,
            // tokenAddresses: tokenAddresses,
            // filterSpamTokens: false,
            // includeFiat: false,
            // includeNativeBalance: true,

            accountAddress: this.dynRemote.primaryWallet$.value.address,
            networkId: chain.id,
            tokenAddresses,
            "chainName": "EVM",
            "includeFiat": true,
            "includeNativeBalance": true,
            "filterSpamTokens": false,
            "forceRefresh": true
        } satisfies DynUseTokenBalancesOptions;
    }

}
